import { useEffect, useState } from 'preact/hooks';
import { listEvalRuns } from '../lib/api';
import { activeToken } from '../store/global';
import type { EvalRun } from '../types/agno-api';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { formatRelative } from '../lib/format';

export function EvalRuns() {
  const [items, setItems] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!activeToken.value) return;
    setLoading(true);
    try {
      const res = await listEvalRuns();
      const data = Array.isArray(res) ? res : (res as { data: EvalRun[] }).data || [];
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [activeToken.value]);

  return (
    <div class="mx-auto max-w-7xl px-6 py-6">
      <PageHeader
        title="Avaliações"
        description="Execuções de eval contra golden cases e LLM como juiz."
        icon={<i class="ph ph-graduation-cap text-xl" />}
        actions={
          <button
            class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        }
      />
      <DataTable
        rows={items as unknown as Array<Record<string, unknown>>}
        rowKey={(r) => (r as unknown as EvalRun).id}
        columns={[
          { key: 'name', label: 'Eval', render: (r) => <span class="font-medium">{(r as unknown as EvalRun).name || (r as unknown as EvalRun).id}</span> },
          {
            key: 'status',
            label: 'Status',
            width: '14ch',
            render: (r) => {
              const s = (r as unknown as EvalRun).status;
              const tone =
                s === 'completed' ? 'text-success' : s === 'failed' ? 'text-destructive' : s === 'running' ? 'text-warning' : 'text-muted-foreground';
              return <span class={`text-xs font-medium ${tone}`}>{s || '—'}</span>;
            },
          },
          {
            key: 'metrics',
            label: 'Métricas',
            render: (r) => {
              const m = (r as unknown as EvalRun).metrics;
              if (!m) return <span class="text-muted-foreground">—</span>;
              return (
                <code class="text-xs font-mono tabular-nums text-muted-foreground">{Object.entries(m).slice(0, 3).map(([k, v]) => `${k}=${String(v)}`).join(' · ')}</code>
              );
            },
          },
          { key: 'created', label: 'Criado', render: (r) => <span class="text-xs text-muted-foreground tabular-nums">{formatRelative((r as unknown as EvalRun).created_at)}</span>, width: '14ch' },
        ]}
        empty={activeToken.value ? 'Nenhuma avaliação executada. Rode um eval para ver os resultados aqui.' : 'Configure o token nas configurações para listar avaliações.'}
      />
    </div>
  );
}
